import {useState} from 'react';
import {useSelector} from 'react-redux';

import {isNilOrEmpty} from 'ramda-adjunct';

import {datagridApi} from '@dms/api/core';
import {selectActiveBranchId} from '@dms/api/features';
import {BusinessCaseType} from '@dms/api/sales';
import {useLazyDataQuery} from '@dms/shared';

export const useKanbanControl = () => {
  const [businessCaseType, setBusinessCaseType] = useState<BusinessCaseType>('SELLING');

  const branchId = useSelector(selectActiveBranchId);

  const [
    getBusinessCaseQueryParams,
    {
      data: businessCaseQuery,
      isLoading: isLoadingBusinessCaseQuery,
      isFetching: isFetchingBusinessCaseQuery,
      isError: isLoadingBusinessCaseQueryErrored,
    },
  ] = useLazyDataQuery('business-case');

  const [
    getInterestQueryParams,
    {
      data: interestQuery,
      isLoading: isLoadingInterestQuery,
      isFetching: isFetchingInterestQuery,
      isError: isLoadingInterestQueryErrored,
    },
  ] = useLazyDataQuery('interest');

  const {
    data: businessCases,
    isLoading: isLoadingBusinessCases,
    isFetching: isFetchingBusinessCases,
    isError: isLoadingBusinessCasesErrored,
  } = datagridApi.useGetDataByDataQueryQuery(
    {
      gridCode: 'business-case',
      dataQueryId: businessCaseQuery?.dataQueryId!,
      offset: 0,
      limit: 1000,
      body: {
        columns: [
          'businessCaseState',
          'vehicle',
          'customerName',
          'tag',
          'comment',
          'businessCasePrice',
          'vehiclePriceWithVat',
          'businessCaseUpdatedAt',
          'businessCaseCreatedAt',
          'owner',
        ],
      },
    },
    {
      skip: isNilOrEmpty(businessCaseQuery?.dataQueryId),
      refetchOnMountOrArgChange: true,
      pollingInterval: KANBAN_POLLING_INTERVAL,
    }
  );

  const {
    data: interests,
    isLoading: isLoadingInterests,
    isFetching: isFetchingInterests,
    isError: isLoadingInterestsErrored,
  } = datagridApi.useGetDataByDataQueryQuery(
    {
      gridCode: 'interest',
      dataQueryId: interestQuery?.dataQueryId!,
      offset: 0,
      limit: 1000,
      body: {
        columns: [
          'state',
          'type',
          'customer',
          'tag',
          'owner',
          'createdAt',
          'updatedAt',
          'comment',
          'vehicle',
        ],
      },
    },
    {
      skip: isNilOrEmpty(interestQuery?.dataQueryId),
      refetchOnMountOrArgChange: true,
      pollingInterval: KANBAN_POLLING_INTERVAL,
    }
  );

  const onFilterChange = async (businessCaseType: BusinessCaseType, filteredUserIds: string[]) => {
    if (isNilOrEmpty(filteredUserIds)) {
      return;
    }

    await getBusinessCaseQueryParams({
      filters: {
        owner: filteredUserIds,
        businessCaseType: [businessCaseType],
        businessCaseState: [
          'OFFER',
          'INVOICE',
          'CONCEPT',
          'CONTRACT',
          'DEPOSIT',
          'DEPOSIT_PAID',
          'PAID',
        ],
        branch: [branchId],
      },
      order: [
        {
          columnKey: 'businessCaseCreatedAt',
          order: 'DESC',
        },
      ],
      smartSearch: null,
    });

    await getInterestQueryParams({
      filters: {
        owner: filteredUserIds,
        type: [businessCaseType],
        state: ['NEW', 'OPEN'],
        branch: [branchId],
      },
      order: [{columnKey: 'createdAt', order: 'DESC'}],
      smartSearch: null,
    });

    setBusinessCaseType(businessCaseType);
  };

  return {
    businessCaseType,
    businessCases,
    interests,
    isLoading:
      isLoadingInterestQuery ||
      isLoadingBusinessCaseQuery ||
      isLoadingBusinessCases ||
      isLoadingInterests,
    isFetching:
      isFetchingBusinessCaseQuery ||
      isFetchingInterestQuery ||
      isFetchingBusinessCases ||
      isFetchingInterests,
    isError:
      isLoadingInterestQueryErrored ||
      isLoadingBusinessCaseQueryErrored ||
      isLoadingBusinessCasesErrored ||
      isLoadingInterestsErrored,
    onFilterChange,
  };
};

/**
 * Polling interval for updates in milliseconds (60 minutes)
 * This is used to refresh the data in the kanban
 * This is needed because the kanban is not updated when the data is updated
 * and the data is not updated in the kanban when the user is not in the kanban
 *
 */
const KANBAN_POLLING_INTERVAL = 60 * 60 * 1000;
